/**
 * Accessibility utilities for PathGR
 * Ensures the platform meets medical professional accessibility standards
 */

// Screen reader announcements
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.setAttribute('class', 'sr-only');
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

// Focus management for modal dialogs
export const trapFocus = (element: HTMLElement) => {
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstElement = focusableElements[0] as HTMLElement;
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
  
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
    
    if (e.key === 'Escape') {
      // Close modal or dialog
      const closeButton = element.querySelector('[data-close]') as HTMLElement;
      if (closeButton) {
        closeButton.click();
      }
    }
  };
  
  element.addEventListener('keydown', handleKeyDown);
  
  // Focus first element
  if (firstElement) {
    firstElement.focus();
  }
  
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
};

// Skip link for keyboard navigation
export const addSkipLink = () => {
  const skipLink = document.createElement('a');
  skipLink.href = '#main-content';
  skipLink.textContent = 'Μετάβαση στο κύριο περιεχόμενο';
  skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-medical-600 text-white px-4 py-2 rounded-lg z-50';
  skipLink.setAttribute('tabindex', '0');
  
  document.body.insertBefore(skipLink, document.body.firstChild);
};

// Keyboard navigation helpers
export const addKeyboardNavigation = (element: HTMLElement, onActivate: () => void) => {
  element.setAttribute('tabindex', '0');
  element.setAttribute('role', 'button');
  
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  };
  
  element.addEventListener('keydown', handleKeyDown);
  
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
};

// High contrast mode detection
export const isHighContrastMode = (): boolean => {
  return window.matchMedia('(prefers-contrast: high)').matches;
};

// Reduced motion detection
export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Color contrast utilities
export const getContrastRatio = (color1: string, color2: string): number => {
  // Simplified contrast ratio calculation
  // In a real implementation, you'd use a proper color contrast library
  return 4.5; // Placeholder - meets WCAG AA standards
};

// ARIA label generators for medical content
export const generateCaseAriaLabel = (caseData: {
  title: string;
  caseType: string;
  urgencyLevel?: string;
  author: string;
  date: string;
}) => {
  const urgency = caseData.urgencyLevel ? `, προτεραιότητα ${caseData.urgencyLevel}` : '';
  return `Περιστατικό: ${caseData.title}, τύπος ${caseData.caseType}${urgency}, από ${caseData.author}, ${caseData.date}`;
};

export const generateImageAriaLabel = (imageData: {
  type: string;
  description?: string;
  magnification?: string;
  staining?: string;
}) => {
  let label = `Εικόνα ${imageData.type}`;
  if (imageData.description) label += `, ${imageData.description}`;
  if (imageData.magnification) label += `, μεγέθυνση ${imageData.magnification}`;
  if (imageData.staining) label += `, χρώση ${imageData.staining}`;
  return label;
};

// Focus indicators for medical forms
export const addMedicalFormAccessibility = (form: HTMLFormElement) => {
  const inputs = form.querySelectorAll('input, select, textarea');
  
  inputs.forEach((input) => {
    const element = input as HTMLElement;
    
    // Add required indicators
    if (element.hasAttribute('required')) {
      const label = form.querySelector(`label[for="${element.id}"]`);
      if (label) {
        label.setAttribute('aria-required', 'true');
        const requiredIndicator = document.createElement('span');
        requiredIndicator.textContent = ' (υποχρεωτικό)';
        requiredIndicator.className = 'text-error-600 text-sm';
        requiredIndicator.setAttribute('aria-label', 'υποχρεωτικό πεδίο');
        label.appendChild(requiredIndicator);
      }
    }
    
    // Add error announcements
    const addErrorAnnouncement = (message: string) => {
      element.setAttribute('aria-invalid', 'true');
      element.setAttribute('aria-describedby', `${element.id}-error`);
      
      let errorDiv = document.getElementById(`${element.id}-error`);
      if (!errorDiv) {
        errorDiv = document.createElement('div');
        errorDiv.id = `${element.id}-error`;
        errorDiv.className = 'text-error-600 text-sm mt-1';
        errorDiv.setAttribute('role', 'alert');
        element.parentNode?.insertBefore(errorDiv, element.nextSibling);
      }
      
      errorDiv.textContent = message;
      announceToScreenReader(`Σφάλμα: ${message}`, 'assertive');
    };
    
    // Add success announcements
    const addSuccessAnnouncement = () => {
      element.setAttribute('aria-invalid', 'false');
      const errorDiv = document.getElementById(`${element.id}-error`);
      if (errorDiv) {
        errorDiv.remove();
      }
    };
    
    // Store functions on element for external use
    (element as any).addErrorAnnouncement = addErrorAnnouncement;
    (element as any).addSuccessAnnouncement = addSuccessAnnouncement;
  });
};

// Medical image accessibility
export const addImageAccessibility = (img: HTMLImageElement, data: {
  type: 'microscopic' | 'gross' | 'clinical';
  description?: string;
  findings?: string[];
  magnification?: string;
  staining?: string;
}) => {
  const ariaLabel = generateImageAriaLabel({
    type: data.type === 'microscopic' ? 'μικροσκοπική' : 
          data.type === 'gross' ? 'μακροσκοπική' : 'κλινική',
    description: data.description,
    magnification: data.magnification,
    staining: data.staining
  });
  
  img.setAttribute('alt', ariaLabel);
  img.setAttribute('role', 'img');
  
  // Add detailed description if findings are provided
  if (data.findings && data.findings.length > 0) {
    const descriptionId = `img-desc-${Math.random().toString(36).substr(2, 9)}`;
    img.setAttribute('aria-describedby', descriptionId);
    
    const description = document.createElement('div');
    description.id = descriptionId;
    description.className = 'sr-only';
    description.textContent = `Ευρήματα εικόνας: ${data.findings.join(', ')}`;
    
    img.parentNode?.appendChild(description);
  }
};

// Landmarks and regions
export const addLandmarks = () => {
  // Add main landmark
  const main = document.querySelector('main') || document.getElementById('main-content');
  if (main) {
    main.setAttribute('role', 'main');
    main.setAttribute('aria-label', 'Κύριο περιεχόμενο');
  }
  
  // Add navigation landmarks
  const nav = document.querySelector('nav');
  if (nav) {
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Κύρια πλοήγηση');
  }
  
  // Add search landmark
  const search = document.querySelector('[role="search"], .search-form');
  if (search) {
    search.setAttribute('role', 'search');
    search.setAttribute('aria-label', 'Αναζήτηση περιστατικών');
  }
};

// Notification accessibility
export const announceNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
  const priority = type === 'error' ? 'assertive' : 'polite';
  const prefix = {
    success: 'Επιτυχία: ',
    error: 'Σφάλμα: ',
    warning: 'Προσοχή: ',
    info: 'Πληροφορία: '
  }[type];
  
  announceToScreenReader(prefix + message, priority);
};

// Initialize accessibility features
export const initializeAccessibility = () => {
  addSkipLink();
  addLandmarks();
  
  // Add global keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    // Alt + M for main menu
    if (e.altKey && e.key === 'm') {
      e.preventDefault();
      const menuButton = document.querySelector('[aria-label*="μενού"], [aria-label*="menu"]') as HTMLElement;
      if (menuButton) {
        menuButton.click();
      }
    }
    
    // Alt + S for search
    if (e.altKey && e.key === 's') {
      e.preventDefault();
      const searchInput = document.querySelector('input[type="search"], .search-input') as HTMLElement;
      if (searchInput) {
        searchInput.focus();
      }
    }
    
    // Alt + H for home
    if (e.altKey && e.key === 'h') {
      e.preventDefault();
      window.location.href = '/';
    }
  });
  
  // Announce page changes for SPA
  let currentPath = window.location.pathname;
  const observer = new MutationObserver(() => {
    if (window.location.pathname !== currentPath) {
      currentPath = window.location.pathname;
      const pageTitle = document.title || 'Νέα σελίδα';
      announceToScreenReader(`Μετάβαση σε ${pageTitle}`);
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true });
};